import { Button } from "@/components/ui";
import { distributionLevels } from "@/lib/utils";
import { setDistributionJobDtl } from "@/redux/slices";
import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import { useGetDistributionJobsDetails, useGetDistributionJobsSimulate } from "@/services";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

const useViewJobDetails = () => {
  const {id} = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const communityData = useSelector((state: RootState) => state.currentData.communityData);
  const currentFinanceYear = useSelector((state: RootState) => state.currentData.currentFinanceYear);
  const distributionJobDtl = useSelector((state: RootState) => state.currentData.distributionJobDtl);
  const communityName = communityData?.name || communityData?.communityName || "";
  const isSimulate = distributionJobDtl?.isSimulate;
  const isPlanned = distributionJobDtl?.budgetType?.toLowerCase() == "plan";
  const selectRowDocs = useRef<any>(null);
  const [openDocDialog, setOpenDocDialog] = useState<boolean>(false);

  const breadcrumbData = useMemo(
    () => [
      {
        link: `/${routes.cam}/${routes.activityDashboard}`,
        name: `Activity Dashboard - ${communityName}`,
      },
      {
        link: `/${routes.cam}/${routes.activityDashboard}/${routes.distributionRuntimeDetails}`,
        name: 'Allocation Run Time Details',
      },
      {
        link: "",
        name: <span className="text-primary">{isSimulate ? "Simulate Job" : `View Run Details (${id})`}</span>,
      },
    ],
    [communityName]
  );

  const title = isSimulate ? <span>Simulate Job</span> : <span>View Run Details <span className="font-normal">({id})</span> - {communityName}({currentFinanceYear})</span>;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);
  
  const {data: jobDtl, isLoading: isLoadingJobDtl} = useGetDistributionJobsDetails({
    communityId: communityData?.communityId,
    jobId: id as string,
    queryParams: {
      enabled: !!(id && !isSimulate)
    }
  });

  const {data: simulateDetails, isLoading: isLoadingJobSimulate} = useGetDistributionJobsSimulate({
    communityId: communityData?.communityId,
    jobId: id as string,
    queryParams: {
      enabled: !!(id && isSimulate)
    }
  });

  const jobDetails = isSimulate ? simulateDetails?.data?.glSummary : jobDtl?.data;
  const isLoadingJobDetails = isSimulate ? isLoadingJobSimulate : isLoadingJobDtl;

  const columns = useMemo(() => ([
    {
      accessorKey: "glMasterId",
      header: <div>GL</div>,
      cell: ({row}: any) => <Button
        variant="link" 
        className="mx-0 px-0"
        onClick={(e: any) => {
          e.preventDefault();
          e.stopPropagation();
          dispatch(setDistributionJobDtl({...distributionJobDtl, simulateDtl: simulateDetails?.data, glMasterId: row.original?.glMasterId, glAccountNumber: row.original?.glAccountNumber, receiverLevel: row?.original?.receiverLevel}))
          navigate(`${row?.original?.glMasterId}`)
        }}>
          {String(row.original?.glAccountNumber || "")}
        </Button>,
    },
    ...(isPlanned ? [] :[{
      accessorKey: "postDocuments",
      header: <div>Doc ID / Line ID</div>,
      cell: ({row}: any) => {
        return <Button variant="link" className="p-0" onClick={() => {
          selectRowDocs.current = row?.original?.postDocuments;
          setOpenDocDialog(true);
        }}>
          Link
        </Button>
      }
    }]),
    {
      accessorKey: "senderLevel",
      header: <div>Sender</div>,
      accessorFn: (row: any) => String(row?.senderLevel || ""),
    },
    {
      accessorKey: "receiverLevel",
      header: <div>Receiver</div>,
      accessorFn: (row: any) => String(row?.receiverLevel || ""),
    },
    {
      accessorKey: "rule",
      header: <div>Rule</div>,
      accessorFn: (row: any) => distributionLevels[row?.rule] || "",
    },
  ]), [simulateDetails]);

  const docColumns = [
    {
      accessorKey: "documentNumber",
      header: <div>Doc ID</div>,
      accessorFn: (row: any) => String(row?.documentNumber || ""),
    },
    {
      accessorKey: "postDocumentLineItemIds",
      header: <div>Line ID(s)</div>,
      accessorFn: (row: any) => row?.postDocumentLineItemIds?.map((_: any, index: number) => index + 1)?.join(", "),
    },
  ];

  return {
    columns,
    title,
    breadcrumbData,
    isLoadingJobDetails,
    jobDetails: jobDetails || [],
    distributionJobDtl,
    isSimulate,
    selectRowDocs: selectRowDocs.current,
    openDocDialog,
    setOpenDocDialog,
    docColumns
  }
}

export default useViewJobDetails;